import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../firebase';

const functions = getFunctions(app, 'europe-west1');

if (process.env.NODE_ENV === 'development') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
  } catch {
    // Already connected
  }
}

class BokioAuth {
  constructor() {
    this.getConfigFunction = httpsCallable(functions, 'getBokioOAuthConfig');
    this.exchangeCodeFunction = httpsCallable(functions, 'exchangeBokioCode');
    this.getStatusFunction = httpsCallable(functions, 'getBokioStatus');
    this.verifyFunction = httpsCallable(functions, 'verifyBokioConnection');
    this.refreshTokenFunction = httpsCallable(functions, 'refreshBokioToken');
    this.syncDataFunction = httpsCallable(functions, 'syncBokioData', { timeout: 540000 });
  }

  generateSecureState() {
    if (typeof window !== 'undefined' && window.crypto?.getRandomValues) {
      const array = new Uint32Array(4);
      window.crypto.getRandomValues(array);
      return Array.from(array, (dec) => dec.toString(36)).join('');
    }
    return Math.random().toString(36).slice(2, 15) + Math.random().toString(36).slice(2, 15) + Date.now().toString(36);
  }

  async getAuthorizationUrl(state = null) {
    const configResult = await this.getConfigFunction();
    const config = configResult.data || {};
    const clientId = config.clientId || process.env.REACT_APP_BOKIO_CLIENT_ID || '';
    const redirectUri = config.redirectUri
      || process.env.REACT_APP_BOKIO_REDIRECT_URI
      || (typeof window !== 'undefined' ? `${window.location.origin}/bokio/callback` : 'https://app.svenry.com/bokio/callback');
    const scope = config.scope || 'company-information:read supplier-invoices:read suppliers:read customers:read uploads:read';
    const authorizeUrl = config.authorizeUrl || 'https://api.bokio.se/v1/authorize';

    if (!clientId) {
      throw new Error('Bokio Client ID not configured. Add BOKIO_CLIENT_ID to Secret Manager.');
    }

    const stateValue = state || this.generateSecureState();
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.setItem('bokio_oauth_state', stateValue);
      sessionStorage.setItem('bokio_oauth_redirect_uri', redirectUri);
    }

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope,
      state: stateValue,
    });

    return `${authorizeUrl}?${params.toString()}`;
  }

  async exchangeCodeForToken(code, state) {
    const redirectUri = (typeof window !== 'undefined' && window.sessionStorage)
      ? sessionStorage.getItem('bokio_oauth_redirect_uri')
      : null;
    const result = await this.exchangeCodeFunction({
      code,
      redirectUri: redirectUri || undefined,
      state,
    });
    if (typeof window !== 'undefined' && window.sessionStorage) {
      sessionStorage.removeItem('bokio_oauth_redirect_uri');
    }
    return result.data;
  }

  async getConnectionStatus() {
    const result = await this.getStatusFunction();
    return result.data;
  }

  async verifyConnection() {
    const result = await this.verifyFunction();
    return result.data;
  }

  async refreshAccessToken() {
    const result = await this.refreshTokenFunction();
    return result.data;
  }

  async syncData() {
    const result = await this.syncDataFunction();
    return result.data;
  }

  async connect() {
    const state = this.generateSecureState();
    window.location.href = await this.getAuthorizationUrl(state);
  }
}

const bokioAuth = new BokioAuth();
export default bokioAuth;
